import { LitElement, html, nothing, type CSSResultGroup, type PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import styles from './styles.js';
import type { Placement } from '../types.js';
import { PlacementController } from '../utils/placement.controller.js';
import { FormAssociatedMixin } from '../utils/form-associated.mixin.js';

export type ColorFormat = 'hex' | 'rgb' | 'hsb';

// ── Color math ────────────────────────────────────────────────────────────────

interface RGB { r: number; g: number; b: number; }
interface HSV { h: number; s: number; v: number; }

function hexToRgb(hex: string): RGB | null {
  const c = hex.replace('#', '');
  if (c.length === 3) {
    return { r: parseInt(c[0]+c[0],16), g: parseInt(c[1]+c[1],16), b: parseInt(c[2]+c[2],16) };
  }
  if (c.length === 6) {
    return { r: parseInt(c.slice(0,2),16), g: parseInt(c.slice(2,4),16), b: parseInt(c.slice(4,6),16) };
  }
  return null;
}

function rgbToHex({ r, g, b }: RGB): string {
  return '#' + [r,g,b].map(v => Math.round(Math.max(0,Math.min(255,v))).toString(16).padStart(2,'0')).join('');
}

function rgbToHsv({ r, g, b }: RGB): HSV {
  const rr = r/255, gg = g/255, bb = b/255;
  const max = Math.max(rr,gg,bb), min = Math.min(rr,gg,bb), d = max-min;
  let h = 0;
  const s = max === 0 ? 0 : d/max;
  const v = max;
  if (d > 0) {
    if      (max === rr) h = ((gg-bb)/d + (gg<bb ? 6 : 0)) / 6;
    else if (max === gg) h = ((bb-rr)/d + 2) / 6;
    else                 h = ((rr-gg)/d + 4) / 6;
  }
  return { h: h*360, s: s*100, v: v*100 };
}

function hsvToRgb({ h, s, v }: HSV): RGB {
  const ss = s/100, vv = v/100;
  const f  = (n: number) => {
    const k = (n + h/60) % 6;
    return vv - vv*ss*Math.max(0, Math.min(k, 4-k, 1));
  };
  return { r: Math.round(f(5)*255), g: Math.round(f(3)*255), b: Math.round(f(1)*255) };
}

function parseColor(value: string, format: ColorFormat): HSV | null {
  if (format === 'hex') {
    const rgb = hexToRgb(value); return rgb ? rgbToHsv(rgb) : null;
  }
  if (format === 'rgb') {
    const m = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    return m ? rgbToHsv({ r:+m[1], g:+m[2], b:+m[3] }) : null;
  }
  if (format === 'hsb') {
    const m = value.match(/hsb\((\d+),\s*(\d+),\s*(\d+)\)/);
    return m ? { h:+m[1], s:+m[2], v:+m[3] } : null;
  }
  return null;
}

function hsvToOutput(hsv: HSV, format: ColorFormat, alpha: number): string {
  const rgb = hsvToRgb(hsv);
  if (format === 'rgb') {
    return alpha < 1
      ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha.toFixed(2)})`
      : `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  }
  if (format === 'hsb') return `hsb(${Math.round(hsv.h)}, ${Math.round(hsv.s)}, ${Math.round(hsv.v)})`;
  return rgbToHex(rgb);
}

/**
 * uwc-colorpicker
 *
 * Full-featured color picker using PlacementController + HTML popover.
 * The panel lives in the browser top layer — it is never clipped by overflow.
 *
 * @attr value         - Current color (hex string by default).
 * @attr format        - hex | rgb | hsb. Default: hex.
 * @attr placement     - Panel placement. Default: bottom-start.
 * @attr offset        - Gap between swatch and panel. Default: 6.
 * @attr inline        - Render panel inline (no swatch trigger, always visible).
 * @attr disabled      - Disable the picker.
 * @attr default-color - Fallback initial color. Default: #ff0000.
 * @attr show-alpha    - Show the alpha transparency slider.
 *
 * @fires uwc-change - Fired when the color changes.
 *   detail: { value, format, rgb: {r,g,b}, hex, alpha }
 * @fires uwc-show   - Panel opened.
 * @fires uwc-hide   - Panel closed.
 *
 * @csspart swatch - The color swatch trigger button.
 * @csspart panel  - The picker panel (popover).
 *
 * @cssprop --uwc-colorpicker-swatch-size   - Swatch size. Default: 2rem.
 * @cssprop --uwc-colorpicker-swatch-radius - Swatch radius.
 * @cssprop --uwc-colorpicker-panel-width   - Panel width. Default: 13.5rem.
 * @cssprop --uwc-cp-duration               - Animation duration. Default: 140ms.
 */
export class UwcColorPicker extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  // ── PlacementHost interface ───────────────────────────────────────

  @property({ reflect: true }) placement: Placement = 'bottom-start';
  @property({ type: Number })  offset = 6;

  // ── Public API ────────────────────────────────────────────────────

  @property()                                 value        = '#ff0000';
  @property({ reflect: true })                format: ColorFormat = 'hex';
  @property({ type: Boolean, reflect: true }) inline       = false;
  @property({ type: Boolean, reflect: true }) disabled     = false;
  @property({ attribute: 'default-color' })   defaultColor = '#ff0000';
  @property({ type: Boolean, attribute: 'show-alpha' }) showAlpha = false;

  // ── Queries ───────────────────────────────────────────────────────

  @query('.uwc-cp__swatch')      private _swatchEl!:  HTMLElement;
  @query('.uwc-cp__panel')       private _panelEl!:   HTMLElement;
  @query('.uwc-cp__area')        private _areaEl!:    HTMLElement;
  @query('.uwc-cp__hue-track')   private _hueEl!:     HTMLElement;
  @query('.uwc-cp__alpha-track') private _alphaEl?: HTMLElement;

  // ── Internal state ────────────────────────────────────────────────

  private _hsv: HSV   = { h: 0, s: 100, v: 100 };
  private _alpha      = 1;
  private _hexInput   = '';
  private _rgbR = 255; private _rgbG = 0; private _rgbB = 0;
  private _hsbH = 0;   private _hsbS = 100; private _hsbV = 100;

  // ── PlacementController ───────────────────────────────────────────

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panelEl ?? null,

    onTriggerFound: (el) => {
      const onClick = (e: Event) => {
        e.stopPropagation();
        this._pc.toggle();
      };
      const onKeydown = (e: KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ') && !this._pc.open) {
          e.preventDefault();
          this._pc.show();
        }
      };
      el.addEventListener('click',   onClick);
      el.addEventListener('keydown', onKeydown as EventListener);
      return () => {
        el.removeEventListener('click',   onClick);
        el.removeEventListener('keydown', onKeydown as EventListener);
      };
    },

    dismissOnOutsideClick: true,
    dismissOnEscape:       true,

    afterShow: () => this.dispatchEvent(new CustomEvent('uwc-show', { bubbles: true, composed: true })),
    afterHide: () => this.dispatchEvent(new CustomEvent('uwc-hide', { bubbles: true, composed: true })),
  });

  // ── Lifecycle ─────────────────────────────────────────────────────

  override firstUpdated(): void {
    this._parseValue();
    if (!this.inline) {
      this._pc.setTriggerElement(this._swatchEl);
    }
    this._internals.setFormValue(this.value || null);
  }

  protected override updated(changed: PropertyValues): void {
    this._pc.updated(changed as Map<PropertyKey, unknown>, null, null);
    if (changed.has('value') && !this._dragging) {
      this._parseValue();
    }
  }

  // dragging flag: prevents value→parse feedback loop during drag
  private _dragging = false;

  // ── Value parsing ─────────────────────────────────────────────────

  private _parseValue() {
    const hsv = parseColor(this.value, this.format)
              ?? parseColor(this.defaultColor, 'hex')
              ?? { h:0, s:100, v:100 };
    this._hsv = hsv;
    this._syncInputFields();
    this.requestUpdate();
  }

  private _syncInputFields() {
    const rgb     = hsvToRgb(this._hsv);
    this._hexInput = rgbToHex(rgb);
    this._rgbR = rgb.r; this._rgbG = rgb.g; this._rgbB = rgb.b;
    this._hsbH = Math.round(this._hsv.h);
    this._hsbS = Math.round(this._hsv.s);
    this._hsbV = Math.round(this._hsv.v);
  }

  // ── Apply color ───────────────────────────────────────────────────

  private _applyHsv(hsv: HSV) {
    this._hsv = { ...hsv };
    this._syncInputFields();
    const out = hsvToOutput(this._hsv, this.format, this._alpha);
    this.value = out;
    const rgb = hsvToRgb(this._hsv);
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { value: out, format: this.format, rgb, hex: rgbToHex(rgb), alpha: this._alpha },
    }));
    this._internals.setFormValue(out);
    this.requestUpdate();
  }

  // ── Area drag ─────────────────────────────────────────────────────

  private _areaPointerDown(e: PointerEvent) {
    e.preventDefault();
    this._dragging = true;
    this._updateArea(e);
    const move = (ev: PointerEvent) => this._updateArea(ev);
    const up   = () => {
      this._dragging = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  private _updateArea(e: PointerEvent) {
    const rect = this._areaEl.getBoundingClientRect();
    const s    = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))  * 100;
    const v    = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height)) * 100;
    this._applyHsv({ h: this._hsv.h, s, v });
  }

  // ── Hue drag ──────────────────────────────────────────────────────

  private _huePointerDown(e: PointerEvent) {
    e.preventDefault();
    this._dragging = true;
    this._updateHue(e);
    const move = (ev: PointerEvent) => this._updateHue(ev);
    const up   = () => {
      this._dragging = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  private _updateHue(e: PointerEvent) {
    const rect = this._hueEl.getBoundingClientRect();
    const h    = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * 360;
    this._applyHsv({ ...this._hsv, h });
  }

  // ── Alpha drag ────────────────────────────────────────────────────

  private _alphaPointerDown(e: PointerEvent) {
    if (!this._alphaEl) return;
    e.preventDefault();
    this._dragging = true;
    this._updateAlpha(e);
    const move = (ev: PointerEvent) => this._updateAlpha(ev);
    const up   = () => {
      this._dragging = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  private _updateAlpha(e: PointerEvent) {
    if (!this._alphaEl) return;
    const rect   = this._alphaEl.getBoundingClientRect();
    this._alpha  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    this._applyHsv(this._hsv);
  }

  // ── Text input handlers ───────────────────────────────────────────

  private _onHexInput(e: Event) {
    this._hexInput = (e.target as HTMLInputElement).value;
    const rgb = hexToRgb(this._hexInput);
    if (rgb) this._applyHsv(rgbToHsv(rgb));
  }

  private _onRgbInput(ch: 'r'|'g'|'b', e: Event) {
    const v = parseInt((e.target as HTMLInputElement).value, 10);
    if (isNaN(v)) return;
    const rgb: RGB = { r:this._rgbR, g:this._rgbG, b:this._rgbB };
    rgb[ch] = Math.max(0, Math.min(255, v));
    this._applyHsv(rgbToHsv(rgb));
  }

  private _onHsbInput(ch: 'h'|'s'|'v', e: Event) {
    const val = parseInt((e.target as HTMLInputElement).value, 10);
    if (isNaN(val)) return;
    const hsv: HSV = { ...this._hsv };
    const limits: Record<string, [number,number]> = { h:[0,360], s:[0,100], v:[0,100] };
    const [min, max] = limits[ch];
    (hsv as unknown as Record<string, number>)[ch] = Math.max(min, Math.min(max, val));
    this._applyHsv(hsv);
  }

  private _cycleFormat() {
    const all: ColorFormat[] = ['hex','rgb','hsb'];
    this.format = all[(all.indexOf(this.format) + 1) % all.length];
    this.value  = hsvToOutput(this._hsv, this.format, this._alpha);
    this.requestUpdate();
  }

  // ── Derived helpers ───────────────────────────────────────────────

  private get _currentHex() { return rgbToHex(hsvToRgb(this._hsv)); }

  // ── Render ────────────────────────────────────────────────────────

  private _renderInputs() {
    if (this.format === 'hex') {
      return html`
        <div class="uwc-cp__input-group">
          <div class="uwc-cp__input-wrap" style="flex:1">
            <input class="uwc-cp__input-field" .value=${this._hexInput}
              @change=${this._onHexInput} maxlength="7" />
            <span class="uwc-cp__input-label">HEX</span>
          </div>
        </div>`;
    }
    if (this.format === 'rgb') {
      return html`
        <div class="uwc-cp__input-group">
          ${(['r','g','b'] as const).map(ch => html`
            <div class="uwc-cp__input-wrap">
              <input class="uwc-cp__input-field" type="number" min="0" max="255"
                .value=${String(ch==='r'?this._rgbR:ch==='g'?this._rgbG:this._rgbB)}
                @change=${(e:Event)=>this._onRgbInput(ch,e)} />
              <span class="uwc-cp__input-label">${ch.toUpperCase()}</span>
            </div>`)}
        </div>`;
    }
    return html`
      <div class="uwc-cp__input-group">
        ${(['h','s','v'] as const).map(ch => html`
          <div class="uwc-cp__input-wrap">
            <input class="uwc-cp__input-field" type="number"
              min="0" max=${ch==='h'?360:100}
              .value=${String(ch==='h'?this._hsbH:ch==='s'?this._hsbS:this._hsbV)}
              @change=${(e:Event)=>this._onHsbInput(ch,e)} />
            <span class="uwc-cp__input-label">${ch==='v'?'B':ch.toUpperCase()}</span>
          </div>`)}
      </div>`;
  }

  override render() {
    const hex  = this._currentHex;
    const open = this._pc.open;
    const rgb  = hsvToRgb(this._hsv);

    return html`
      <!-- Swatch trigger (hidden in inline mode via CSS) -->
      <div
        part="swatch"
        class=${classMap({ 'uwc-cp__swatch': true, 'uwc-cp__swatch--open': open })}
        tabindex=${this.disabled ? '-1' : '0'}
        role="button"
        aria-label="Pick color"
        aria-expanded=${open}
        style="--_swatch-bg:${hex}"
      ></div>

      <!-- Panel — popover="manual" lives in browser top layer -->
      <div
        part="panel"
        class=${classMap({ 'uwc-cp__panel': true, 'is-open': open || this.inline })}
        popover="manual"
        data-placement=${this.placement}
      >
        <!-- SV color area -->
        <div
          class="uwc-cp__area"
          style="--_hue:${this._hsv.h}"
          @pointerdown=${this._areaPointerDown}
        >
          <div class="uwc-cp__area-hue"></div>
          <div class="uwc-cp__area-dark"></div>
          <div class="uwc-cp__cursor" style=${styleMap({
            left: `${this._hsv.s}%`,
            top:  `${100 - this._hsv.v}%`,
          })}></div>
        </div>

        <!-- Preview swatch + sliders -->
        <div class="uwc-cp__preview-row">
          <div class="uwc-cp__preview-swatch" style="background:${hex}"></div>
          <div class="uwc-cp__sliders">
            <!-- Hue -->
            <div class="uwc-cp__hue-track" @pointerdown=${this._huePointerDown}>
              <div class="uwc-cp__thumb" style=${styleMap({
                left: `${this._hsv.h / 360 * 100}%`,
              })}></div>
            </div>
            <!-- Alpha (optional) -->
            ${this.showAlpha ? html`
              <div class="uwc-cp__alpha-track"
                style=${styleMap({ '--_alpha-color': `rgb(${rgb.r},${rgb.g},${rgb.b})` } as Record<string,string>)}
                @pointerdown=${this._alphaPointerDown}>
                <div class="uwc-cp__thumb" style=${styleMap({
                  left: `${this._alpha * 100}%`,
                })}></div>
              </div>` : nothing}
          </div>
        </div>

        <!-- Format inputs -->
        <div class="uwc-cp__inputs">
          <!-- Column wrapper mirrors the input-wrap structure so bottoms align -->
          <div class="uwc-cp__format-col">
            <button class="uwc-cp__format-btn" type="button"
              title="Cycle format (${this.format})"
              @click=${this._cycleFormat}>
              ${this.format.toUpperCase()}
            </button>
            <span class="uwc-cp__format-spacer" aria-hidden="true"></span>
          </div>
          ${this._renderInputs()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-colorpicker': UwcColorPicker; }
}
